import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, computed, ElementRef, inject, InjectionToken, Injector, OnDestroy, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EgTooltip } from '@expertgroup/shell';
import { I18n } from '@/app/core/i18n/i18n';
import { TranslationKey } from '@/app/core/i18n/translations';
import { GlobalSearchService, SearchGroupKey, SearchHit } from './global-search.service';

/** How long typing must pause before the groups are asked (D15: 250 ms). A token so a spec can run without the wait. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = new InjectionToken<number>('GLOBAL_SEARCH_DEBOUNCE_MS', { providedIn: 'root', factory: () => 250 });

/** A group of the open palette: the typed groups, or the recently opened tenants before anything is typed. */
export type PaletteGroupKey = SearchGroupKey | 'recent';

/** One group's state. `loading` covers the debounce too: an answer for an older term never shows under a newer one. */
export type GroupState = { readonly status: 'loading' } | { readonly status: 'error' } | { readonly status: 'done'; readonly hits: readonly SearchHit[]; readonly total: number };

/** One result row; `key` is its DOM id, the value of the input's `aria-activedescendant`. */
export interface PaletteOption {
    readonly key: string;
    readonly group: PaletteGroupKey;
    readonly hit: SearchHit;
}

export interface PaletteGroup {
    readonly key: PaletteGroupKey;
    readonly label: TranslationKey;
    readonly icon: string;
    readonly state: GroupState;
    readonly options: readonly PaletteOption[];
}

const LOADING: GroupState = { status: 'loading' };
const FAILED: GroupState = { status: 'error' };

const GROUP_LABELS: Readonly<Record<PaletteGroupKey, TranslationKey>> = {
    recent: 'search.groups.recent',
    tenants: 'search.groups.tenants',
    users: 'search.groups.users',
    applications: 'search.groups.applications',
    clients: 'search.groups.clients'
};

const GROUP_ICONS: Readonly<Record<PaletteGroupKey, string>> = {
    recent: 'ph-clock-counter-clockwise',
    tenants: 'ph-buildings',
    users: 'ph-user',
    applications: 'ph-squares-four',
    clients: 'ph-key'
};

/** What the trigger's placeholder names, per group (accusative in Serbian: „Pretraži tenante, korisnike, …"). */
const TARGETS: Readonly<Record<SearchGroupKey, TranslationKey>> = {
    tenants: 'layout.search.targets.tenants',
    users: 'layout.search.targets.users',
    applications: 'layout.search.targets.applications',
    clients: 'layout.search.targets.clients'
};

/** Whether the keyboard event is the palette's shortcut: Ctrl+K, or Cmd+K on macOS (either is accepted everywhere). */
export function isPaletteShortcut(event: KeyboardEvent): boolean {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return false;
    // `code` too: under a Cyrillic layout `key` is „к", the physical key is still K.
    return event.key?.toLowerCase() === 'k' || event.code === 'KeyK';
}

/** The shortcut as the trigger shows it and as `aria-keyshortcuts` names it, for the platform the page runs on. */
export function paletteShortcut(navigatorLike: { platform?: string; userAgentData?: { platform?: string } } | undefined): { label: string; aria: string } {
    const platform = navigatorLike?.userAgentData?.platform || navigatorLike?.platform || '';
    return /mac|iphone|ipad|ipod/i.test(platform) ? { label: 'Cmd K', aria: 'Meta+K' } : { label: 'Ctrl K', aria: 'Control+K' };
}

/**
 * The global search (UX pregled 1, D15): the topbar trigger and the command palette it opens. Who sees which group, and the
 * reads, are `GlobalSearchService`'s; this is the interaction.
 *
 * - **Trigger:** the shell's search field, 340 px, with the shortcut in a key cap; below 1200 px an icon button with the
 *   shell tooltip naming the shortcut. Both are `aria-haspopup="dialog"` and carry `aria-keyshortcuts`.
 * - **Shortcut:** Ctrl/Cmd+K on `document` toggles the palette and prevents the browser's own (address/search bar). It does
 *   nothing while a PrimeNG modal is open — the palette never stacks over another dialog.
 * - **Palette:** a modal dialog with one combobox input over a listbox of groups (ARIA combobox with
 *   `aria-activedescendant`; the input keeps the focus, the options are never focused). Each group is its own read,
 *   debounced 250 ms, with its own loading rows, inline error and empty line — a failing group never touches another, and
 *   an answer for an older term is dropped. Before typing: the recently opened tenants (D13), else a hint.
 * - **Keyboard:** ↑/↓ across all groups (wrapping), Enter opens, Tab stays in the dialog, Escape closes. Escape is stopped
 *   at the dialog: the service-graph drill-down and the shell launcher close on ANY document Escape, and closing the
 *   palette on /ekosistem must not also drop the selected node.
 * - **Focus:** opening moves it to the input; closing returns it to where it was (the trigger, or the element focused when
 *   Ctrl+K was pressed), or to the visible trigger when that element is gone.
 * - **Opening a result is navigation only:** `/tenanti/:id`, `/korisnici/:id`, `/aplikacije/:id` (an OAuth client: its
 *   application's page at `#klijenti`, UX pregled 3 D-UX3-18). No token, sign-in tenant,
 *   scope or actor changes.
 */
@Component({
    selector: 'eg-global-search',
    standalone: true,
    imports: [EgTooltip],
    templateUrl: './global-search.component.html',
    styleUrl: './global-search.component.scss',
    host: { '(document:keydown)': 'onDocumentKeydown($event)' }
})
export class GlobalSearchComponent implements OnDestroy {
    private readonly search = inject(GlobalSearchService);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);
    private readonly document = inject(DOCUMENT);
    private readonly debounceMs = inject(GLOBAL_SEARCH_DEBOUNCE_MS);
    readonly i18n = inject(I18n);

    readonly shortcut = paletteShortcut(this.document.defaultView?.navigator as Parameters<typeof paletteShortcut>[0]);
    readonly listboxId = 'id-search-listbox';

    readonly open = signal(false);
    readonly term = signal('');
    /** The option the keyboard (or the mouse) is on; falls back to the first option when it is no longer listed. */
    readonly activeKey = signal<string | null>(null);

    private readonly recentState = signal<GroupState | null>(null);
    private readonly groupStates = signal<Partial<Record<SearchGroupKey, GroupState>>>({});

    private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');
    private readonly wideTrigger = viewChild.required<ElementRef<HTMLButtonElement>>('wideTrigger');
    private readonly compactTrigger = viewChild.required<ElementRef<HTMLButtonElement>>('compactTrigger');

    private timer: ReturnType<typeof setTimeout> | undefined;
    /** Bumped on every keystroke and on close: an answer carrying an older number never lands. */
    private sequence = 0;
    /** Bumped on every opening and close, for the recently opened tenants' read. */
    private recentSequence = 0;
    private returnFocus: HTMLElement | null = null;

    readonly placeholder = computed(() => {
        const targets = this.search
            .groups()
            .map((group) => this.i18n.t(TARGETS[group]))
            .join(', ');
        return this.i18n.t('layout.search.placeholder', { targets });
    });

    readonly typed = computed(() => this.term().trim().length > 0);

    readonly visibleGroups = computed<readonly PaletteGroup[]>(() => {
        if (this.typed()) {
            const states = this.groupStates();
            return this.search.groups().map((key) => this.group(key, states[key] ?? LOADING));
        }
        const recent = this.recentState();
        // An empty recent list (nothing recorded, or every id gone from the server) is the hint, not an empty group.
        if (!recent || (recent.status === 'done' && recent.hits.length === 0)) return [];
        return [this.group('recent', recent)];
    });

    readonly options = computed<readonly PaletteOption[]>(() => this.visibleGroups().flatMap((group) => group.options));

    readonly activeOption = computed<PaletteOption | null>(() => {
        const options = this.options();
        const key = this.activeKey();
        return options.find((option) => option.key === key) ?? options[0] ?? null;
    });

    /** One polite live region: searching, how many results, which groups failed. */
    readonly announcement = computed(() => {
        const groups = this.visibleGroups();
        if (!this.typed() || groups.length === 0) return '';
        if (groups.some((group) => group.state.status === 'loading')) return this.i18n.t('search.announce.loading');
        const results = this.i18n.t('search.announce.results', { count: this.options().length });
        const failed = groups.filter((group) => group.state.status === 'error').map((group) => this.i18n.t(group.label));
        return failed.length ? `${results} ${this.i18n.t('search.announce.failed', { groups: failed.join(', ') })}` : results;
    });

    ngOnDestroy(): void {
        clearTimeout(this.timer);
    }

    /** The name a row shows; a deleted tenant or account is marked in words, never by colour alone. */
    label(hit: SearchHit): string {
        return hit.deleted ? this.i18n.t('search.deleted', { name: hit.name }) : hit.name;
    }

    /** „5 od 23" when the group holds more than it shows (narrowed by typing, never paged). */
    shownOf(group: PaletteGroup): string | null {
        if (group.state.status !== 'done' || group.state.total <= group.state.hits.length) return null;
        return this.i18n.t('search.shownOf', { shown: group.state.hits.length, total: group.state.total });
    }

    openPalette(): void {
        if (this.open() || !this.search.available()) return;
        const focused = this.document.activeElement;
        this.returnFocus = focused instanceof HTMLElement && focused !== this.document.body ? focused : null;
        this.open.set(true);
        this.loadRecent();
        afterNextRender({ write: () => this.input()?.nativeElement.focus() }, { injector: this.injector });
    }

    close(): void {
        if (!this.open()) return;
        clearTimeout(this.timer);
        this.sequence++;
        this.recentSequence++;
        this.open.set(false);
        this.term.set('');
        this.activeKey.set(null);
        this.groupStates.set({});
        this.recentState.set(null);
        const target = this.returnFocus?.isConnected ? this.returnFocus : this.visibleTrigger();
        this.returnFocus = null;
        target.focus();
    }

    onDocumentKeydown(event: KeyboardEvent): void {
        if (!isPaletteShortcut(event)) return;
        // Not ours to take: nothing to search for this actor, or another modal dialog is open (the palette never stacks on it).
        if (!this.open() && (!this.search.available() || this.document.querySelector('.p-overlay-mask'))) return;
        event.preventDefault();
        if (event.repeat) return;
        if (this.open()) this.close();
        else this.openPalette();
    }

    /**
     * A press anywhere in the dialog but the input would move the focus to the page behind it (a group heading, the
     * padding), and the next Escape would then reach `document` — the drill-down's listener — instead of this dialog.
     */
    keepFocus(event: MouseEvent): void {
        if (event.target !== this.input()?.nativeElement) event.preventDefault();
    }

    /** Keys inside the dialog; the input has the focus, so they all arrive here. */
    onPaletteKeydown(event: KeyboardEvent): void {
        if (event.isComposing) return;
        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                event.stopPropagation();
                this.close();
                return;
            case 'ArrowDown':
            case 'ArrowUp':
                event.preventDefault();
                this.move(event.key === 'ArrowDown' ? 1 : -1);
                return;
            case 'Enter': {
                const option = this.activeOption();
                if (!option) return;
                event.preventDefault();
                this.activate(option);
                return;
            }
            case 'Tab':
                // A modal dialog keeps the focus; its only stop is the input.
                event.preventDefault();
                return;
        }
    }

    onInput(value: string): void {
        this.term.set(value);
        this.activeKey.set(null);
        clearTimeout(this.timer);
        const sequence = ++this.sequence;
        const term = value.trim();
        if (!term) {
            this.groupStates.set({});
            return;
        }
        const groups = this.search.groups();
        this.groupStates.set(Object.fromEntries(groups.map((group) => [group, LOADING])));
        this.timer = setTimeout(() => {
            for (const group of groups) {
                this.search.search(group, term).then(
                    (outcome) => this.settle(sequence, group, { status: 'done', hits: outcome.hits, total: outcome.total }),
                    () => this.settle(sequence, group, FAILED)
                );
            }
        }, this.debounceMs);
    }

    /** Opens a result: the palette closes first (an overlay left over the destination reads as a failed jump), then navigates. */
    activate(option: PaletteOption): void {
        const { commands, queryParams, fragment } = option.hit.link;
        this.close();
        void this.router.navigate([...commands], { ...(queryParams ? { queryParams } : {}), ...(fragment ? { fragment } : {}) });
    }

    private settle(sequence: number, group: SearchGroupKey, state: GroupState): void {
        if (sequence !== this.sequence) return;
        this.groupStates.update((states) => ({ ...states, [group]: state }));
    }

    private loadRecent(): void {
        const ids = this.search.recentIds();
        if (ids.length === 0) return;
        const sequence = ++this.recentSequence;
        this.recentState.set(LOADING);
        this.search.recent(ids).then(
            (outcome) => {
                if (sequence === this.recentSequence) this.recentState.set({ status: 'done', hits: outcome.hits, total: outcome.total });
            },
            () => {
                if (sequence === this.recentSequence) this.recentState.set(FAILED);
            }
        );
    }

    private move(delta: number): void {
        const options = this.options();
        const current = this.activeOption();
        if (!current) return;
        const next = options[(options.indexOf(current) + delta + options.length) % options.length];
        this.activeKey.set(next.key);
        afterNextRender({ read: () => this.document.getElementById(next.key)?.scrollIntoView({ block: 'nearest' }) }, { injector: this.injector });
    }

    private group(key: PaletteGroupKey, state: GroupState): PaletteGroup {
        const options = state.status === 'done' ? state.hits.map((hit) => ({ key: `id-search-${key}-${hit.id}`, group: key, hit })) : [];
        return { key, label: GROUP_LABELS[key], icon: GROUP_ICONS[key], state, options };
    }

    /** The trigger the layout shows at this width (the other one is `display: none`). */
    private visibleTrigger(): HTMLButtonElement {
        const wide = this.wideTrigger().nativeElement;
        return getComputedStyle(wide).display === 'none' ? this.compactTrigger().nativeElement : wide;
    }
}
